import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, UserPlus, UserCog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  useCreateUserMutation,
  useUpdateUserMutation,
  type UserResponse,
} from '@/features/user';
import { toast } from 'sonner';

// Schema for add mode (with password)
const addUserSchema = z
  .object({
    nrp: z.string().min(1, 'NRP is required'),
    fullname: z.string().min(1, 'Full name is required'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

// Schema for edit mode (without password)
const editUserSchema = z.object({
  nrp: z.string().min(1, 'NRP is required'),
  fullname: z.string().min(1, 'Full name is required'),
});

type AddUserFormValues = z.infer<typeof addUserSchema>;
type EditUserFormValues = z.infer<typeof editUserSchema>;

interface AddUserModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly mode?: 'add' | 'edit';
  readonly user?: UserResponse | null;
}

export function AddUserModal({
  isOpen,
  onClose,
  mode = 'add',
  user = null,
}: AddUserModalProps) {
  const createUserMutation = useCreateUserMutation();
  const updateUserMutation = useUpdateUserMutation();

  const isEditMode = mode === 'edit' && user;

  // Create separate form hooks for add and edit modes
  const addForm = useForm<AddUserFormValues>({
    resolver: zodResolver(addUserSchema),
    mode: 'onChange',
    defaultValues: {
      nrp: '',
      fullname: '',
      password: '',
      confirmPassword: '',
    },
  });

  const editForm = useForm<EditUserFormValues>({
    resolver: zodResolver(editUserSchema),
    mode: 'onChange',
    defaultValues: {
      nrp: user?.nrp || '',
      fullname: user?.fullname || '',
    },
  });

  // Watch password fields only in add mode
  const password = isEditMode ? undefined : addForm.watch('password');
  const confirmPassword = isEditMode
    ? undefined
    : addForm.watch('confirmPassword');

  // Trigger validation on confirmPassword when password changes (only for add mode)
  useEffect(() => {
    if (!isEditMode && confirmPassword) {
      addForm.trigger('confirmPassword');
    }
  }, [password, confirmPassword, isEditMode, addForm]);

  // Reset form when user changes or modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && user) {
        editForm.reset({
          nrp: user.nrp,
          fullname: user.fullname,
        });
      } else {
        addForm.reset({
          nrp: '',
          fullname: '',
          password: '',
          confirmPassword: '',
        });
      }
    }
  }, [isOpen, isEditMode, user, addForm, editForm]);

  const onSubmitAdd = async (values: AddUserFormValues) => {
    try {
      await createUserMutation.mutateAsync({
        nrp: values.nrp,
        fullname: values.fullname,
        password: values.password,
        role: 'USER', // Always USER, admin is created by BE
      });
      toast.success('User created successfully');
      addForm.reset();
      onClose();
    } catch (error: any) {
      console.error('[User] Create failed:', error);
      toast.error(error?.message || 'Failed to create user');
    }
  };

  const onSubmitEdit = async (values: EditUserFormValues) => {
    if (!user) return;
    try {
      await updateUserMutation.mutateAsync({
        id: user.userId,
        data: {
          nrp: values.nrp,
          fullname: values.fullname,
        },
      });
      toast.success('User updated successfully');
      editForm.reset();
      onClose();
    } catch (error: any) {
      console.error('[User] Update failed:', error);
      toast.error(error?.message || 'Failed to update user');
    }
  };

  const onSubmit = isEditMode
    ? editForm.handleSubmit(onSubmitEdit)
    : addForm.handleSubmit(onSubmitAdd);

  const handleCancel = () => {
    if (isEditMode) {
      editForm.reset();
    } else {
      addForm.reset();
    }
    onClose();
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isPending =
    createUserMutation.isPending || updateUserMutation.isPending;

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        className="fixed inset-0 bg-black/50 z-40 border-0 cursor-default"
        onClick={handleCancel}
        aria-label="Close modal"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
        <div className="bg-slate-900 rounded-lg border border-slate-700 shadow-2xl w-full max-w-md mx-4 pointer-events-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              {isEditMode ? (
                <UserCog className="w-5 h-5 text-blue-400" />
              ) : (
                <UserPlus className="w-5 h-5 text-blue-400" />
              )}
              <h2
                id="user-modal-title"
                className="text-lg font-semibold text-white"
              >
                {isEditMode ? 'Edit User' : 'Add New User'}
              </h2>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="text-slate-400 hover:text-white p-1 h-auto"
              aria-label="Close modal"
              disabled={isPending}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Content */}
          <form onSubmit={onSubmit}>
            <div className="px-6 py-6 space-y-4">
              {/* NRP */}
              <div className="space-y-2">
                <Label>
                  NRP <span className="text-rose-400">*</span>
                </Label>
                <Input
                  type="text"
                  placeholder="Enter NRP"
                  {...(isEditMode
                    ? editForm.register('nrp')
                    : addForm.register('nrp'))}
                  className={cn(
                    (isEditMode
                      ? editForm.formState.errors.nrp
                      : addForm.formState.errors.nrp) &&
                      'border-red-500 focus-visible:ring-red-500'
                  )}
                />
                {(isEditMode
                  ? editForm.formState.errors.nrp
                  : addForm.formState.errors.nrp) && (
                  <p className="text-xs text-rose-400">
                    {
                      (isEditMode
                        ? editForm.formState.errors.nrp
                        : addForm.formState.errors.nrp
                      )?.message
                    }
                  </p>
                )}
              </div>

              {/* Fullname */}
              <div className="space-y-2">
                <Label>
                  Full Name <span className="text-rose-400">*</span>
                </Label>
                <Input
                  type="text"
                  placeholder="Enter full name"
                  {...(isEditMode
                    ? editForm.register('fullname')
                    : addForm.register('fullname'))}
                  className={cn(
                    (isEditMode
                      ? editForm.formState.errors.fullname
                      : addForm.formState.errors.fullname) &&
                      'border-red-500 focus-visible:ring-red-500'
                  )}
                />
                {(isEditMode
                  ? editForm.formState.errors.fullname
                  : addForm.formState.errors.fullname) && (
                  <p className="text-xs text-rose-400">
                    {
                      (isEditMode
                        ? editForm.formState.errors.fullname
                        : addForm.formState.errors.fullname
                      )?.message
                    }
                  </p>
                )}
              </div>

              {/* Password fields - only show in add mode */}
              {!isEditMode && (
                <>
                  {/* Password */}
                  <div className="space-y-2">
                    <Label>
                      Password <span className="text-rose-400">*</span>
                    </Label>
                    <Input
                      type="password"
                      placeholder="Enter password (min 8 characters)"
                      showPasswordToggle
                      {...addForm.register('password')}
                      className={cn(
                        addForm.formState.errors.password &&
                          'border-red-500 focus-visible:ring-red-500'
                      )}
                    />
                    {addForm.formState.errors.password && (
                      <p className="text-xs text-rose-400">
                        {addForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="space-y-2">
                    <Label>
                      Confirm Password <span className="text-rose-400">*</span>
                    </Label>
                    <Input
                      type="password"
                      placeholder="Confirm password"
                      showPasswordToggle
                      {...addForm.register('confirmPassword', {
                        onChange: () => {
                          // Trigger validation on change
                          addForm.trigger('confirmPassword');
                        },
                      })}
                      className={cn(
                        addForm.formState.errors.confirmPassword &&
                          'border-red-500 focus-visible:ring-red-500'
                      )}
                    />
                    {addForm.formState.errors.confirmPassword && (
                      <p className="text-xs text-rose-400">
                        {addForm.formState.errors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isPending}
                className={cn(isPending && 'opacity-80 cursor-not-allowed')}
              >
                {(() => {
                  if (isPending) {
                    return isEditMode ? 'Updating...' : 'Creating...';
                  }
                  return isEditMode ? 'Update User' : 'Create User';
                })()}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
